import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ConsequenceReveal from '../shared/ConsequenceReveal';
import styles from './StressThermometerEngine.module.css';

const StressThermometerEngine = ({ gamifiedQuestion, onAnswer, accent = '#264653' }) => {
  const [visible, setVisible] = useState([]);
  const [cleared, setCleared] = useState([]);
  const [showOutcome, setShowOutcome] = useState(false);
  const [answerLabel, setAnswerLabel] = useState(null);
  const [startedAt, setStartedAt] = useState(Date.now());
  const [canClick, setCanClick] = useState(false);
  const [sceneReady, setSceneReady] = useState(false);

  const options = useMemo(() => gamifiedQuestion?.options?.slice(0, 5) || [], [gamifiedQuestion?.options]);
  const selectedOption = options.find((option) => option.label === answerLabel);

  // Initialize with notifications flying in from right
  useEffect(() => {
    setVisible([]);
    setCleared([]);
    setShowOutcome(false);
    setAnswerLabel(null);
    setCanClick(false);
    setSceneReady(false);

    const startTime = Date.now();
    setStartedAt(startTime);
    setSceneReady(true);

    // Stagger notifications flying in from right
    const timers = options.map((_, index) =>
      setTimeout(() => {
        setVisible((prev) => [...prev, index]);
      }, 400 + index * 200)
    );

    // Enable clicking after 1000ms
    const clickTimer = setTimeout(() => {
      setCanClick(true);
    }, 1000);

    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(clickTimer);
    };
  }, [options]);

  // Auto-complete if all cleared
  useEffect(() => {
    if (!options.length || cleared.length === 0 || answerLabel) return;

    if (cleared.length === visible.length && visible.length > 0) {
      const elapsed = Date.now() - startedAt;
      // Map elapsed time to label (faster = higher priority label like A)
      const label = elapsed < 5000 ? 'A' : elapsed < 7000 ? 'B' : elapsed < 9000 ? 'C' : elapsed < 11000 ? 'D' : 'E';
      setAnswerLabel(label);
      setTimeout(() => setShowOutcome(true), 500);
    }
  }, [answerLabel, cleared.length, options.length, startedAt, visible.length]);

  const stressRatio = Math.max((visible.length - cleared.length) / Math.max(options.length, 1), 0);

  const handleNotificationClick = (index) => {
    if (!canClick || !sceneReady || cleared.includes(index)) return;
    setCleared((prev) => [...prev, index]);
  };

  return (
    <div className={styles.engine} style={{ '--accent': accent }}>
      {/* SCENE AREA - Dark panel with notifications */}
      <div
        className={styles.sceneArea}
        style={{
          background: stressRatio > 0.6 ? '#fef3c7' : stressRatio > 0.3 ? '#ffedd5' : '#e0f2fe'
        }}
      >
        <div className={styles.sceneContent}>
          {/* Left side - Teacher/Character */}
          <motion.div
            className={styles.teacherWrap}
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <svg viewBox="0 0 100 120" className={styles.teacherSvg}>
              {/* Teacher figure */}
              <circle cx="50" cy="30" r="15" fill="#fff" stroke="#1a1a1a" strokeWidth="2" />
              <line x1="50" y1="45" x2="50" y2="90" stroke="#1a1a1a" strokeWidth="2" />
              <line x1="35" y1="60" x2="65" y2="60" stroke="#1a1a1a" strokeWidth="2" />
              {/* Expression changes with stress */}
              {stressRatio > 0.6 ? (
                // Stressed face
                <>
                  <circle cx="44" cy="28" r="2" fill="#1a1a1a" />
                  <circle cx="56" cy="28" r="2" fill="#1a1a1a" />
                  <path d="M 44 38 Q 50 35 56 38" fill="none" stroke="#1a1a1a" strokeWidth="2" />
                  {/* Sweat drops */}
                  <motion.text x="70" y="25" fontSize="14" animate={{ y: [0, 3, 0] }} transition={{ repeat: Infinity, duration: 1 }}>
                    💧
                  </motion.text>
                </>
              ) : stressRatio > 0.3 ? (
                // Concerned face
                <>
                  <circle cx="44" cy="28" r="2" fill="#1a1a1a" />
                  <circle cx="56" cy="28" r="2" fill="#1a1a1a" />
                  <line x1="44" y1="36" x2="56" y2="36" stroke="#1a1a1a" strokeWidth="2" />
                </>
              ) : (
                // Calm face
                <>
                  <circle cx="44" cy="28" r="2" fill="#1a1a1a" />
                  <circle cx="56" cy="28" r="2" fill="#1a1a1a" />
                  <path d="M 44 36 Q 50 40 56 36" fill="none" stroke="#1a1a1a" strokeWidth="2" />
                </>
              )}
            </svg>
          </motion.div>

          {/* Middle - Stress meter */}
          <motion.div
            className={styles.thermo}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <span className={styles.thermoLabel}>Stress</span>
            <div className={styles.thermoTube}>
              <motion.div
                className={styles.mercury}
                animate={{ height: `${Math.round(stressRatio * 100)}%` }}
                transition={{ type: 'spring', stiffness: 100 }}
              />
            </div>
            <span className={styles.thermoValue}>{Math.round(stressRatio * 100)}%</span>
          </motion.div>

          {/* Right - Notifications stack */}
          <div className={styles.notificationLayer}>
            <AnimatePresence>
              {options.map((option, index) => {
                if (!visible.includes(index)) return null;
                const isCleared = cleared.includes(index);
                const yOffset = index * 55 - cleared.filter((i) => i < index).length * 55;

                return (
                  <motion.button
                    key={option.label}
                    type="button"
                    className={`${styles.notice} ${isCleared ? styles.dismissed : ''}`}
                    initial={{ x: 300, opacity: 0 }}
                    animate={{
                      x: isCleared ? 100 : 0,
                      y: yOffset,
                      opacity: isCleared ? 0 : 1,
                      scale: isCleared ? 0.8 : 1
                    }}
                    exit={{ x: 100, opacity: 0 }}
                    transition={{
                      type: 'spring',
                      stiffness: 200,
                      damping: 20
                    }}
                    whileHover={!isCleared && canClick ? { scale: 1.02, x: -5 } : {}}
                    onClick={() => handleNotificationClick(index)}
                    disabled={!canClick || isCleared}
                  >
                    <div className={styles.noticeHeader}>
                      <span className={styles.noticeIcon}>🔔</span>
                      <strong>{option.label}</strong>
                    </div>
                    <span className={styles.noticeText}>{option.actionText || option.text}</span>
                    {!canClick && (
                      <motion.div
                        className={styles.clickBlocker}
                        initial={{ opacity: 1 }}
                        animate={{ opacity: 0 }}
                        transition={{ delay: 1, duration: 0.3 }}
                      />
                    )}
                  </motion.button>
                );
              })}
            </AnimatePresence>

            {/* Timer hint */}
            <motion.div
              className={styles.timerHint}
              initial={{ opacity: 0 }}
              animate={{ opacity: canClick ? 0 : 1 }}
            >
              Notifications arriving...
            </motion.div>
          </div>
        </div>
      </div>

      {/* QUESTION TEXT */}
      <div className={styles.questionText}>
        {gamifiedQuestion?.situation}
      </div>

      {/* ANSWER OPTIONS - Represent the notification priorities */}
      <div className={styles.optionsArea}>
        {options.map((option, index) => {
          const isCleared = cleared.includes(index);
          return (
            <motion.button
              key={option.label}
              type="button"
              className={`${styles.choiceBtn} ${isCleared ? styles.cleared : ''}`}
              initial={{ y: 30, opacity: 0 }}
              animate={{
                y: 0,
                opacity: isCleared ? 0.4 : 1,
                backgroundColor: isCleared ? '#f0f0f0' : '#fff'
              }}
              transition={{ delay: 0.6 + index * 0.1, type: 'spring', stiffness: 100 }}
              whileHover={!isCleared ? { y: -2, borderColor: accent } : {}}
              whileTap={!isCleared ? { scale: 0.98 } : {}}
              onClick={() => handleNotificationClick(index)}
              disabled={!canClick || isCleared}
            >
              <span className={styles.choiceLabel}>{option.label}</span>
              <span className={styles.choiceText}>{option.actionText || option.text}</span>
              {isCleared && (
                <motion.span
                  className={styles.clearedBadge}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 200 }}
                >
                  ✓
                </motion.span>
              )}
            </motion.button>
          );
        })}
      </div>

      {/* Narration */}
      <p className={styles.narration}>{gamifiedQuestion?.narration}</p>

      <ConsequenceReveal
        visible={showOutcome}
        tone={answerLabel === 'A' || answerLabel === 'B' ? 'warning' : 'success'}
        message={selectedOption?.consequence || 'Stress response profile recorded.'}
        duration={1700}
        onDone={() => {
          setShowOutcome(false);
          if (answerLabel) onAnswer(answerLabel);
        }}
      />
    </div>
  );
};

export default StressThermometerEngine;
